import axios from 'axios'

import { constant } from './constant'
import { getSessionModal } from './selector'

export const SessionModal = (() => {
	interface KeepAliveResponse {
		ok: boolean
		expiration: number | null
	}

	interface SecondsLeftResponse {
		ok: boolean
		secondsLeft: number | null
		warnBeforeLogout: number
	}

	let timerId: ReturnType<typeof setTimeout> | null = null
	let logoutTimerId: ReturnType<typeof setTimeout> | null = null
	let countdownIntervalId: ReturnType<typeof setInterval> | null = null

	let initialized = false
	let isLoadingSecondsLeft = false
	let hasLoadedSecondsLeft = false
	let warnBeforeLogout: number | null = null

	const openModal = (modal: HTMLElement): void => {
		modal.classList.remove('hidden')
	}

	const closeModal = (modal: HTMLElement): void => {
		modal.classList.add('hidden')
	}

	const clearTimer = (): void => {
		if (timerId !== null) {
			clearTimeout(timerId)
			timerId = null
		}
	}

	const clearCountdown = (): void => {
		if (countdownIntervalId !== null) {
			clearInterval(countdownIntervalId)
			countdownIntervalId = null
		}
	}

	const clearLogoutTimer = (): void => {
		if (logoutTimerId !== null) {
			clearTimeout(logoutTimerId)
			logoutTimerId = null
		}
	}

	const performLogout = (): void => {
		window.location.reload()
	}

	const formatTime = (totalSeconds: number): string => {
		const minutes = Math.floor(totalSeconds / 60)
		const seconds = totalSeconds % 60
		const mm = String(minutes)
		const ss = String(seconds).padStart(2, '0')
		return `${mm}:${ss}`
	}

	const startCountdown = (el: HTMLElement, deadlineAt: number, onExpire: () => void): void => {
		clearCountdown()
		const tick = () => {
			const now = Date.now()
			const remainingMs = Math.max(0, deadlineAt - now)
			const remainingSec = Math.ceil(remainingMs / 1000)
			el.textContent = formatTime(remainingSec)
			if (remainingSec <= 0) {
				clearCountdown()
				onExpire()
			}
		}
		tick()
		countdownIntervalId = setInterval(tick, 1000)
	}

	const init = (): void => {
		if (initialized) {
			return
		}
		const modal = getSessionModal()
		if (!(modal instanceof HTMLElement)) {
			return
		}

		const stayBtn = modal.querySelector('#stayLoggedIn') as HTMLButtonElement | null
		if (!(stayBtn instanceof HTMLButtonElement)) {
			return
		}

		const keepAliveUrl = modal.getAttribute(constant.selector.url)
		const secondsLeftUrl = modal.getAttribute(constant.selector.secondsLeftUrl)
		let deadlineAt: number | null = null

		if (keepAliveUrl === null) {
			return
		}
		if (secondsLeftUrl === null) {
			return
		}

		initialized = true

		const scheduleWarning = (): void => {
			clearTimer()

			if (deadlineAt === null || warnBeforeLogout === null) {
				return
			}

			const countdownEl = modal.querySelector('#sessionModal-countdown') as HTMLElement | null
			const msUntilWarning = Math.max(0, deadlineAt - Date.now() - warnBeforeLogout * 1000)

			if (msUntilWarning === 0) {
				openModal(modal)
				if (countdownEl instanceof HTMLElement) {
					startCountdown(countdownEl, deadlineAt, () => performLogout())
				}
				return
			}

			timerId = setTimeout((): void => {
				openModal(modal)
				if (countdownEl instanceof HTMLElement && deadlineAt !== null) {
					startCountdown(countdownEl, deadlineAt, () => performLogout())
				}
			}, msUntilWarning)
		}

		const scheduleLogout = (): void => {
			clearLogoutTimer()
			if (deadlineAt === null) {
				return
			}
			const msUntilLogout = Math.max(0, deadlineAt + 5 - Date.now())
			logoutTimerId = setTimeout(() => performLogout(), msUntilLogout)
		}

		const keepAlive = async (): Promise<void> => {
			try {
				const response = await axios.post<KeepAliveResponse>(keepAliveUrl, null, {
					withCredentials: true,
					headers: { 'X-Requested-With': 'XMLHttpRequest' },
				})
				const payload = response?.data
				if (payload?.ok !== true) {
					return
				}

				if (typeof payload.expiration === 'number') {
					deadlineAt = Date.now() + payload.expiration * 1000
				} else {
					deadlineAt = null
				}
				closeModal(modal)
				clearCountdown()
				scheduleWarning()
			} catch {
				/* empty */
			}
		}

		const loadSecondsLeft = async (): Promise<void> => {
			if (isLoadingSecondsLeft || hasLoadedSecondsLeft) {
				return
			}
			isLoadingSecondsLeft = true
			try {
				const response = await axios.get<SecondsLeftResponse>(secondsLeftUrl, {
					withCredentials: true,
					headers: { 'X-Requested-With': 'XMLHttpRequest' },
				})
				const payload = response?.data
				if (payload?.ok !== true) {
					return
				}
				if (typeof payload.secondsLeft === 'number') {
					deadlineAt = Date.now() + payload.secondsLeft * 1000
					warnBeforeLogout = payload.warnBeforeLogout * 60
					scheduleWarning()
					scheduleLogout()
					hasLoadedSecondsLeft = true
				} else {
					hasLoadedSecondsLeft = true
				}
			} catch {
				/* empty */
			} finally {
				isLoadingSecondsLeft = false
			}
		}

		stayBtn.addEventListener('click', (e: MouseEvent) => {
			e.preventDefault()
			keepAlive()
		})

		document.addEventListener('keydown', (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				closeModal(modal)
				clearCountdown()
			}
		})

		void loadSecondsLeft()
	}

	return {
		init,
	}
})()
